import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useAuth } from '../../../contexts/AuthContext';
import SlidePreview from './SlidePreview';
import SlideFilmstrip from './SlideFilmstrip';
import ArcaInspectorPanel from './ArcaInspectorPanel';
import {
  createEmptySlide,
  normalizeSlide,
  duplicateSlide,
} from '../utils/slideSchema';
import { gerarSlideArca, gerarImagemArca, importarArquivoArca, resolveArcaAssetUrl, melhorarSlideArca, gerarDeckArca, salvarApresentacaoArca } from '../utils/arcaApi';
import { exportApresentacaoPptx, exportApresentacaoPdf } from '../utils/exportSlide';
import { loadArcaSettings, saveArcaModelo, saveArcaSettings } from '../utils/arcaStorage';
import { HUD, resolveSlideTheme } from '../utils/slideThemes';
import useArcaHistory from '../utils/useArcaHistory';
import { getTemplateById } from '../utils/arcaTemplates';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';

function mapAsset(asset) {
  const w = Number(asset.w ?? asset.largura) || 32;
  return {
    ...asset,
    src: resolveArcaAssetUrl(asset.url),
    largura: w,
    w,
    x: asset.x != null ? Number(asset.x) : 58,
    y: asset.y != null ? Number(asset.y) : 28,
    zIndex: Number(asset.zIndex) || 2,
  };
}

function fonteLabel(fonte) {
  if (fonte === 'gemini') return 'Gemini IA';
  if (fonte === 'pptx-import') return 'Importado PPTX';
  if (fonte === 'mock') return 'Mock local';
  return null;
}

/** Barra flutuante inferior — manipulação rápida de slides. */
function FloatingSlideToolbar({ onAdd, onDuplicate, onDelete, canDelete, disabled }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
        bgcolor: HUD.bgElevated,
        borderRadius: 3,
        px: 0.75,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        border: `1px solid ${HUD.border}`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
      }}
    >
      <Tooltip title="Novo slide">
        <span>
          <IconButton size="small" onClick={onAdd} disabled={disabled} sx={{ color: HUD.accent }}>
            <AddCircleOutlineIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ borderColor: HUD.border, mx: 0.25 }} />
      <Tooltip title="Duplicar slide">
        <span>
          <IconButton size="small" onClick={onDuplicate} disabled={disabled} sx={{ color: HUD.muted }}>
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Excluir slide">
        <span>
          <IconButton
            size="small"
            onClick={onDelete}
            disabled={disabled || !canDelete}
            sx={{ color: HUD.danger }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

export default function ArcaApresentacoesWorkspace() {
  const previewRef = useRef(null);
  const location = useLocation();
  const { unidadeSelecionada } = useAuth();
  const arcaSettings = useMemo(() => loadArcaSettings(), []);

  const [inspectorTab, setInspectorTab] = useState(0);
  const [modo, setModo] = useState(arcaSettings.modoPadrao || 'livre');
  const [tomIa, setTomIa] = useState(arcaSettings.tomIa || 'executivo');
  const [prompt, setPrompt] = useState('');
  const [deckQuantidade, setDeckQuantidade] = useState(5);
  const [usarWeb, setUsarWeb] = useState(false);
  const { slides, setSlides, loadSlides, undo, redo, canUndo, canRedo } = useArcaHistory([]);
  const [activeSlideId, setActiveSlideId] = useState(null);
  const [apresentacaoNome, setApresentacaoNome] = useState('Apresentação sem título');
  const [apresentacaoId, setApresentacaoId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatingDeck, setGeneratingDeck] = useState(false);
  const [improving, setImproving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingServer, setSavingServer] = useState(false);
  const [isPresenting, setIsPresenting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const activeSlide = useMemo(
    () => slides.find((s) => s.id === activeSlideId) || null,
    [slides, activeSlideId]
  );

  const activeIndex = useMemo(
    () => slides.findIndex((s) => s.id === activeSlideId),
    [slides, activeSlideId]
  );

  const slideTheme = resolveSlideTheme(activeSlide?.theme);

  const notify = useCallback(
    (message, severity = 'info') => setSnack({ open: true, message, severity }),
    []
  );

  useEffect(() => {
    const modelo = location.state?.loadModelo;
    if (!modelo?.slides?.length) return;
    const normalized = modelo.slides.map((s) => normalizeSlide(s)).filter(Boolean);
    loadSlides(normalized);
    setActiveSlideId(normalized[0]?.id || null);
    setApresentacaoNome(modelo.nome || 'Apresentação');
    setApresentacaoId(null);
    setModo(arcaSettings.modoPadrao || 'livre');
    notify(`Modelo «${modelo.nome}» carregado.`, 'success');
    window.history.replaceState({}, document.title);
  }, [location.state, arcaSettings.modoPadrao, notify, loadSlides]);

  const setActiveSlide = useCallback(
    (slide) => {
      if (!slide?.id) return;
      setSlides((prev) => prev.map((s) => (s.id === slide.id ? slide : s)));
    },
    [setSlides]
  );

  const handleAddBlankSlide = useCallback(() => {
    const blank = createEmptySlide();
    blank.layout = arcaSettings.layoutPadrao || blank.layout;
    setSlides((prev) => [...prev, blank]);
    setActiveSlideId(blank.id);
  }, [arcaSettings.layoutPadrao, setSlides]);

  const handleSalvarModelo = () => {
    if (!slides.length) {
      notify('Adicione slides antes de salvar o modelo.', 'warning');
      return;
    }
    saveArcaModelo({ nome: apresentacaoNome, slides });
    notify(`Modelo «${apresentacaoNome}» salvo. Veja em Modelos Salvos.`, 'success');
  };

  const handleSalvarServidor = async () => {
    if (!slides.length) {
      notify('Adicione slides antes de salvar.', 'warning');
      return;
    }
    if (!unidadeSelecionada?.codigo) {
      notify('Selecione uma unidade para salvar no servidor.', 'warning');
      return;
    }
    setSavingServer(true);
    try {
      const saved = await salvarApresentacaoArca({
        id: apresentacaoId,
        nome: apresentacaoNome,
        slides,
        unidade: unidadeSelecionada.codigo,
      });
      setApresentacaoId(saved.id);
      notify(`Apresentação salva no banco (unidade ${unidadeSelecionada.codigo}).`, 'success');
    } catch (e) {
      notify(e.message || 'Erro ao salvar no servidor.', 'error');
    } finally {
      setSavingServer(false);
    }
  };

  const handleApplyTheme = useCallback(
    (themeId) => {
      if (!activeSlide) return;
      setActiveSlide({ ...activeSlide, theme: themeId });
    },
    [activeSlide, setActiveSlide]
  );

  const handleTomIaChange = useCallback((value) => {
    setTomIa(value);
    saveArcaSettings({ tomIa: value });
  }, []);

  const handleGerarSlide = useCallback(async () => {
    if (!prompt.trim()) {
      notify('Digite um prompt antes de gerar o slide.', 'warning');
      return;
    }
    setGenerating(true);
    try {
      const data = await gerarSlideArca({
        prompt,
        modo,
        unidadeCodigo: unidadeSelecionada?.codigo,
        tomIa,
        usarWeb,
      });
      const generated = normalizeSlide(data.slide);
      setSlides((prev) => [...prev, generated]);
      setActiveSlideId(generated.id);
      if (!apresentacaoNome || apresentacaoNome === 'Apresentação sem título') {
        setApresentacaoNome(generated.titulo);
      }
      const chartMsg = generated.graficos?.length > 0 ? ` ${generated.graficos.length} gráfico(s).` : '';
      const webMsg = data.webAviso ? ` ${data.webAviso}` : data.slide?.meta?.web?.ok ? ' Com busca web.' : '';
      notify(
        (modo === 'conectado' ? 'Slide gerado com contexto SGC.' : 'Slide gerado com Gemini free.') +
          chartMsg +
          webMsg,
        data.webAviso ? 'warning' : 'success'
      );
    } catch (e) {
      notify(e.message || 'Erro ao gerar slide.', 'error');
    } finally {
      setGenerating(false);
    }
  }, [prompt, modo, tomIa, usarWeb, apresentacaoNome, unidadeSelecionada?.codigo, notify, setSlides]);

  const handleMelhorarSlide = useCallback(async () => {
    if (!activeSlide) {
      notify('Selecione um slide para melhorar.', 'warning');
      return;
    }
    setImproving(true);
    try {
      const data = await melhorarSlideArca({
        slide: activeSlide,
        tomIa,
        instrucao: prompt.trim() || undefined,
        modo,
        unidadeCodigo: unidadeSelecionada?.codigo,
      });
      const improved = normalizeSlide(data.slide);
      setSlides((prev) => prev.map((s) => (s.id === improved.id ? improved : s)));
      notify('Slide melhorado com Gemini free.', 'success');
    } catch (e) {
      notify(e.message || 'Erro ao melhorar slide.', 'error');
    } finally {
      setImproving(false);
    }
  }, [activeSlide, tomIa, prompt, modo, unidadeSelecionada?.codigo, notify, setSlides]);

  const handleGerarDeck = useCallback(async () => {
    if (!prompt.trim()) {
      notify('Digite o tema da apresentação antes de gerar o deck.', 'warning');
      return;
    }
    setGeneratingDeck(true);
    try {
      const data = await gerarDeckArca({
        prompt,
        modo,
        unidadeCodigo: unidadeSelecionada?.codigo,
        tomIa,
        quantidade: deckQuantidade,
        usarWeb,
      });
      const list = (data.slides || []).map((s) => normalizeSlide(s)).filter(Boolean);
      if (!list.length) {
        notify('A IA não retornou slides.', 'warning');
        return;
      }
      loadSlides(list);
      setActiveSlideId(list[0].id);
      if (!apresentacaoNome || apresentacaoNome === 'Apresentação sem título') {
        setApresentacaoNome(list[0].titulo || 'Apresentação IA');
      }
      notify(
        `Deck gerado: ${list.length} slide(s) com Gemini free.` + (data.webAviso ? ` ${data.webAviso}` : ''),
        data.webAviso ? 'warning' : 'success'
      );
    } catch (e) {
      notify(e.message || 'Erro ao gerar deck.', 'error');
    } finally {
      setGeneratingDeck(false);
    }
  }, [
    prompt,
    modo,
    tomIa,
    usarWeb,
    deckQuantidade,
    apresentacaoNome,
    unidadeSelecionada?.codigo,
    notify,
    loadSlides,
  ]);

  const handleAddTemplate = useCallback(
    (templateId) => {
      const tpl = getTemplateById(templateId);
      if (!tpl) return;
      const slide = normalizeSlide(tpl.build());
      if (activeSlide?.theme) slide.theme = activeSlide.theme;
      setSlides((prev) => [...prev, slide]);
      setActiveSlideId(slide.id);
      notify(`Template «${tpl.label}» adicionado.`, 'success');
    },
    [activeSlide?.theme, notify, setSlides]
  );

  const handleImportStockAsset = useCallback(
    (asset) => {
      if (!activeSlideId) {
        notify('Selecione ou crie um slide antes de importar a foto.', 'warning');
        return;
      }
      const mapped = mapAsset(asset);
      setSlides((prev) =>
        prev.map((s) =>
          s.id === activeSlideId
            ? { ...normalizeSlide(s), midias: [...(s.midias || []), mapped] }
            : s
        )
      );
      notify(`Foto Unsplash adicionada (${asset.licenca?.author || 'stock'}).`, 'success');
    },
    [activeSlideId, notify, setSlides]
  );

  const handleImportFiles = useCallback(
    async (files) => {
      setImporting(true);
      try {
        for (const file of files) {
          const data = await importarArquivoArca(file);
          if (data.tipo === 'pptx') {
            const list = (data.slidesSugeridos?.length ? data.slidesSugeridos : data.slideSugerido ? [data.slideSugerido] : [])
              .map((s) =>
                normalizeSlide({
                  ...s,
                  midias: (s.midias || []).map(mapAsset),
                })
              )
              .filter(Boolean);
            if (!list.length) {
              notify('Nenhum slide extraído do PPTX.', 'warning');
              continue;
            }
            loadSlides(list);
            setActiveSlideId(list[0].id);
            if (list[0].titulo) setApresentacaoNome(list[0].titulo);
            notify(`PPTX importado: ${list.length} slide(s).`, 'success');
          } else if (data.tipo === 'imagem' && data.assets?.[0]) {
            const asset = mapAsset(data.assets[0]);
            const hasTarget = activeSlideId && slides.some((s) => s.id === activeSlideId);
            if (hasTarget) {
              setSlides((prev) =>
                prev.map((s) =>
                  s.id === activeSlideId
                    ? { ...normalizeSlide(s), midias: [...(s.midias || []), asset] }
                    : s
                )
              );
            } else {
              const blank = createEmptySlide();
              blank.midias = [asset];
              setSlides((prev) => [...prev, blank]);
              setActiveSlideId(blank.id);
            }
            notify(`Imagem «${asset.nome}» adicionada.`, 'success');
          }
        }
      } catch (e) {
        notify(e.message || 'Erro ao importar arquivo.', 'error');
      } finally {
        setImporting(false);
      }
    },
    [activeSlideId, slides, notify, setSlides, loadSlides]
  );

  const handleGerarImagem = useCallback(async () => {
    const imgPrompt = prompt.trim() || activeSlide?.titulo?.trim();
    if (!imgPrompt) {
      notify('Digite um prompt ou selecione um slide com título.', 'warning');
      return;
    }
    let targetId = activeSlideId;
    if (!targetId) {
      const blank = createEmptySlide();
      setSlides((prev) => [...prev, blank]);
      targetId = blank.id;
      setActiveSlideId(targetId);
    }
    setGeneratingImage(true);
    try {
      const data = await gerarImagemArca({ prompt: imgPrompt });
      const asset = mapAsset(data.asset);
      setSlides((prev) =>
        prev.map((s) =>
          s.id === targetId ? { ...normalizeSlide(s), midias: [...(s.midias || []), asset] } : s
        )
      );
      notify('Ilustração adicionada ao slide.', 'success');
    } catch (e) {
      const isQuota = e.status === 429 || e.code === 'GEMINI_IMAGE_QUOTA';
      notify(e.message || 'Erro ao gerar ilustração.', isQuota ? 'warning' : 'error');
    } finally {
      setGeneratingImage(false);
    }
  }, [prompt, activeSlide, activeSlideId, notify, setSlides]);

  const handleDuplicateSlide = useCallback(
    (id) => {
      const source = slides.find((s) => s.id === id);
      if (!source) return;
      const copy = duplicateSlide(source);
      setSlides((prev) => {
        const idx = prev.findIndex((s) => s.id === id);
        const next = [...prev];
        next.splice(idx + 1, 0, copy);
        return next;
      });
      setActiveSlideId(copy.id);
    },
    [slides, setSlides]
  );

  const handleRemoveSlide = useCallback(
    (id) => {
      setSlides((prev) => {
        if (prev.length <= 1) return prev;
        const idx = prev.findIndex((s) => s.id === id);
        const next = prev.filter((s) => s.id !== id);
        if (activeSlideId === id) {
          setActiveSlideId(next[Math.max(0, idx - 1)]?.id || null);
        }
        return next;
      });
    },
    [activeSlideId, setSlides]
  );

  const goToSlideOffset = useCallback(
    (delta) => {
      if (!slides.length) return;
      const idx = Math.max(0, slides.findIndex((s) => s.id === activeSlideId));
      const next = slides[(idx + delta + slides.length) % slides.length];
      if (next) setActiveSlideId(next.id);
    },
    [slides, activeSlideId]
  );

  const handlePresent = async () => {
    const el = previewRef.current;
    if (!el || !activeSlide) {
      notify('Selecione um slide para apresentar.', 'warning');
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsPresenting(false);
      } else {
        await el.requestFullscreen();
        setIsPresenting(true);
      }
    } catch {
      notify('Tela cheia indisponível neste navegador.', 'error');
    }
  };

  useEffect(() => {
    const onFs = () => {
      if (!document.fullscreenElement) setIsPresenting(false);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = String(e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }

      if (!slides.length) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        goToSlideOffset(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        goToSlideOffset(-1);
      } else if (e.key === 'Escape' && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length, goToSlideOffset, undo, redo]);

  const handleExportPptx = async () => {
    if (!slides.length) {
      notify('Nenhum slide para exportar.', 'warning');
      return;
    }
    setExporting(true);
    try {
      await exportApresentacaoPptx(slides, apresentacaoNome);
      notify(`PPTX com ${slides.length} slide(s) baixado.`, 'success');
    } catch (e) {
      notify(e.message || 'Erro ao exportar PPTX.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!slides.length || !previewRef.current) {
      notify('Nenhum slide para exportar.', 'warning');
      return;
    }
    setExporting(true);
    const previousId = activeSlideId;
    try {
      await exportApresentacaoPdf(slides, previewRef.current, apresentacaoNome, setActiveSlideId);
      notify(`PDF com ${slides.length} slide(s) salvo.`, 'success');
    } catch (e) {
      notify(e.message || 'Erro ao exportar PDF.', 'error');
    } finally {
      setActiveSlideId(previousId);
      setExporting(false);
    }
  };

  const fonteChip = fonteLabel(activeSlide?.meta?.fonte);
  const busy = generating || generatingDeck || improving || importing || exporting || generatingImage || savingServer;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        width: '100%',
        height: '100%',
        minHeight: 0,
        bgcolor: HUD.bgDeep,
        overflow: 'hidden',
      }}
    >
      {/* Filmstrip esquerda */}
      {slides.length > 0 && (
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            bgcolor: HUD.bgPanel,
            borderRight: `1px solid ${HUD.border}`,
            minHeight: 0,
          }}
        >
          <SlideFilmstrip
            slides={slides}
            activeSlideId={activeSlideId}
            onSelect={setActiveSlideId}
            onAdd={handleAddBlankSlide}
            onDuplicate={handleDuplicateSlide}
            onRemove={handleRemoveSlide}
            onReorder={(next) => setSlides(next)}
            variant="hud"
          />
        </Box>
      )}

      {/* Canvas central */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          position: 'relative',
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            height: 52,
            flexShrink: 0,
            bgcolor: HUD.bgPanel,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            borderBottom: `1px solid ${HUD.border}`,
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Desfazer (Ctrl+Z)">
              <span>
                <IconButton size="small" onClick={undo} disabled={!canUndo || busy} sx={{ color: canUndo ? HUD.muted : HUD.subtle }}>
                  <UndoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refazer (Ctrl+Y)">
              <span>
                <IconButton size="small" onClick={redo} disabled={!canRedo || busy} sx={{ color: canRedo ? HUD.muted : HUD.subtle }}>
                  <RedoIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ borderColor: HUD.border, mx: 0.5 }} />
            <Tooltip title="Slide anterior (←)">
              <span>
                <IconButton size="small" onClick={() => goToSlideOffset(-1)} disabled={slides.length < 2} sx={{ color: HUD.muted }}>
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" sx={{ color: HUD.subtle, minWidth: 48, textAlign: 'center' }}>
              {slides.length ? `${Math.max(1, activeIndex + 1)}/${slides.length}` : '0/0'}
            </Typography>
            <Tooltip title="Próximo slide (→)">
              <span>
                <IconButton size="small" onClick={() => goToSlideOffset(1)} disabled={slides.length < 2} sx={{ color: HUD.muted }}>
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, justifyContent: 'center' }}>
            <Typography variant="subtitle2" noWrap sx={{ color: HUD.text, fontWeight: 600 }}>
              {apresentacaoNome}
            </Typography>
            {slides.length > 0 && (
              <Chip
                label={`${slides.length} slides`}
                size="small"
                sx={{ bgcolor: HUD.bgElevated, color: HUD.muted, height: 22, fontSize: '0.7rem' }}
              />
            )}
            {fonteChip && (
              <Chip
                label={fonteChip}
                size="small"
                sx={{
                  bgcolor: 'rgba(56,189,248,0.15)',
                  color: HUD.accent,
                  height: 22,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Salvar no servidor (unidade)">
              <span>
                <IconButton size="small" onClick={handleSalvarServidor} disabled={!slides.length || savingServer} sx={{ color: HUD.accent }}>
                  {savingServer ? <CircularProgress size={16} /> : <CloudUploadOutlinedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Salvar modelo local">
              <span>
                <IconButton size="small" onClick={handleSalvarModelo} disabled={!slides.length} sx={{ color: HUD.muted }}>
                  <SaveOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Baixar PPTX">
              <span>
                <IconButton size="small" onClick={handleExportPptx} disabled={exporting || !slides.length} sx={{ color: HUD.muted }}>
                  {exporting ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Salvar PDF">
              <span>
                <IconButton size="small" onClick={handleExportPdf} disabled={exporting || !slides.length} sx={{ color: HUD.muted }}>
                  <PictureAsPdfIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isPresenting ? 'Sair da apresentação (Esc)' : 'Apresentar (tela cheia · ← →)'}>
              <IconButton size="small" onClick={handlePresent} sx={{ color: HUD.accent }}>
                <PlayArrowIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Canvas 16:9 contido */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, md: 3 },
            overflow: 'auto',
            bgcolor: HUD.bgPanel,
            containerType: 'size',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              aspectRatio: '16 / 9',
              width: 'min(100cqw, calc(100cqh * 16 / 9))',
              height: 'auto',
              maxHeight: '100cqh',
              maxWidth: '100%',
              bgcolor: slideTheme.surface,
              color: slideTheme.text,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              border: `1px solid ${slideTheme.border}`,
              boxShadow: slideTheme.glow,
              overflow: 'hidden',
            }}
          >
            <SlidePreview
              ref={previewRef}
              slide={activeSlide}
              onChange={setActiveSlide}
              embedded
              themeId={activeSlide?.theme}
              emptyMessage="Gere um slide com IA (aba Configurações) ou clique + abaixo."
            />
          </Paper>
        </Box>

        <FloatingSlideToolbar
          onAdd={handleAddBlankSlide}
          onDuplicate={() => activeSlideId && handleDuplicateSlide(activeSlideId)}
          onDelete={() => activeSlideId && handleRemoveSlide(activeSlideId)}
          canDelete={slides.length > 1}
          disabled={busy}
        />

        {!slides.length && (
          <Box sx={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddBlankSlide}
              sx={{
                textTransform: 'none',
                color: HUD.accent,
                borderColor: HUD.borderAccent,
                '&:hover': { borderColor: HUD.accent, bgcolor: 'rgba(56,189,248,0.08)' },
              }}
            >
              Começar slide em branco
            </Button>
          </Box>
        )}
      </Box>

      {/* Inspetor direito */}
      <ArcaInspectorPanel
        inspectorTab={inspectorTab}
        onTabChange={setInspectorTab}
        activeSlide={activeSlide}
        onSlideChange={setActiveSlide}
        apresentacaoNome={apresentacaoNome}
        onApresentacaoNomeChange={setApresentacaoNome}
        modo={modo}
        onModoChange={setModo}
        tomIa={tomIa}
        onTomIaChange={handleTomIaChange}
        prompt={prompt}
        onPromptChange={setPrompt}
        deckQuantidade={deckQuantidade}
        onDeckQuantidadeChange={setDeckQuantidade}
        onGerarSlide={handleGerarSlide}
        onGerarDeck={handleGerarDeck}
        onMelhorarSlide={handleMelhorarSlide}
        onImportFiles={handleImportFiles}
        onGerarImagem={handleGerarImagem}
        onAddTemplate={handleAddTemplate}
        onImportStockAsset={handleImportStockAsset}
        usarWeb={usarWeb}
        onUsarWebChange={setUsarWeb}
        generating={generating}
        generatingDeck={generatingDeck}
        improving={improving}
        generatingImage={generatingImage}
        importing={importing}
        exporting={exporting}
        showIlustracaoHint={arcaSettings.mostrarDicaIlustracao}
        onApplyTheme={handleApplyTheme}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
